import { getAuthTokens } from "@/store/useAuthStore";
import {
  AppError,
  FactCheckCorrection,
  FormValues,
  SystemMessage,
} from "@/types/types";
import { AI_AGENT_API } from "@/utils/constants";

type StreamCallbackFn = (chunk: string) => void;

interface GhostWriterStreamResponse {
  status?: string;
  chunk?: string;
  system?: SystemMessage;
  corrections?: FactCheckCorrection[];
}

export const generateDraftContent = async (
  formData: FormData,
  onChunkReceived: StreamCallbackFn,
  onComplete: () => void,
  onError: (error: Error) => void,
  onSystemMessage: (msg: SystemMessage) => void,
  signal: AbortSignal,
  onCorrections?: (corrections: FactCheckCorrection[]) => void
): Promise<void> => {
  try {
    const writing_about = formData.get("writing_about")?.toString();

    if (!writing_about) {
      throw new Error("writing about can't be empty");
    }

    const authTokens = getAuthTokens();

    const response = await fetch(`${AI_AGENT_API}/writer/ghostwrite`, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${authTokens?.token}`,
      },
    });

    if (!response.ok) {
      const errResponse = await response.json();
      console.log(errResponse);

      if (errResponse?.detail && errResponse?.error) {
        throw new AppError(
          errResponse?.detail?.detail,
          errResponse?.type || null
        );
      } else if (errResponse?.detail && errResponse?.type) {
        throw new AppError(errResponse?.detail, errResponse?.type);
      } else if (errResponse?.detail && !errResponse?.error) {
        throw new AppError(errResponse?.detail, null);
      }
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("Failed to get stream reader");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;
      buffer += decoder.decode(value);

      if (signal.aborted) {
        reader.cancel();
        throw new DOMException("Aborted", "AbortError");
      }

      let startPos = 0;
      let endPos = 0;

      while ((endPos = findJsonEnd(buffer, startPos)) !== -1) {
        try {
          const jsonStr = buffer.substring(startPos, endPos + 1);
          const parsedJson: GhostWriterStreamResponse = JSON.parse(jsonStr);

          if (parsedJson?.system) {
            onSystemMessage(parsedJson.system);
          }

          if (parsedJson.chunk !== undefined) {
            onChunkReceived(parsedJson.chunk);
          }

          if (parsedJson.corrections) {
            onCorrections?.(parsedJson.corrections);
          }

          startPos = endPos + 1;
        } catch (parseError) {
          console.log("Error parsing JSON:", parseError);
          startPos++;
        }
      }

      // Keeping the buffer small by keeping only unprocessed data
      buffer = buffer.substring(startPos);
    }

    onComplete();
  } catch (error) {
    onError(
      error instanceof Error ? error : new Error("Unknown error occurred")
    );
    throw error;
  }
};

// Helper function to find the end of a JSON object
function findJsonEnd(str: string, startPos: number): number {
  // Find the start of a JSON object
  const objectStart = str.indexOf("{", startPos);
  if (objectStart === -1) return -1;

  let braceCount = 0;
  let inString = false;
  let escapeNext = false;

  for (let i = objectStart; i < str.length; i++) {
    const char = str[i];

    if (escapeNext) {
      escapeNext = false;
      continue;
    }

    if (char === "\\" && inString) {
      escapeNext = true;
      continue;
    }

    if (char === '"' && !escapeNext) {
      inString = !inString;
      continue;
    }

    if (!inString) {
      if (char === "{") {
        braceCount++;
      } else if (char === "}") {
        braceCount--;
        if (braceCount === 0) {
          return i; // Found the end of a complete JSON object
        }
      }
    }
  }

  return -1; // No complete JSON object found
}

export const prepareFormData = (values: FormValues): FormData => {
  const formData = new FormData();

  formData.append("project_id", values.project_id);
  formData.append("target_length", values.target_length as string);
  formData.append("writing_about", values.writing_about);

  if (values.attached_audios_transcripts) {
    const transcriptMap: Record<string, string> = {};

    for (const audio of values.attached_audios_transcripts) {
      if (audio.transcript && audio.file?.name) {
        transcriptMap[audio.file.name] = audio.transcript;
      } else if (audio.file) {
        formData.append("attached_context_files", audio.file);
      }
    }

    if (Object.keys(transcriptMap).length > 0) {
      formData.append(
        "attached_audios_transcripts",
        JSON.stringify(transcriptMap)
      );
    }
  }

  if (values.autofactcheck) {
    formData.append("auto_factcheck", values.autofactcheck ? "true" : "false");
  }

  if (values.attached_links && values.attached_links.length > 0) {
    for (const link of values.attached_links) {
      formData.append("attached_links", link);
    }
  }

  if (values.attached_context_files) {
    if (values.attached_context_files.googleDrive) {
      for (const g of values.attached_context_files.googleDrive) {
        formData.append("attached_context_files", g.file);
      }
    }
    if (values.attached_context_files.uploaded) {
      for (const file of values.attached_context_files.uploaded) {
        formData.append("attached_context_files", file);
      }
    }
  }

  return formData;
};
